import { RAPYD_ENTITIES } from '@rapyd/cp-core/dist/utils/consts';
import { PermissionsProps } from '@rapyd/cp-core';
import isPermittedRole from '@rapyd/cp-core/dist/utils/permissions.utils';
import { isPermittedUserRole } from 'src/utils/userData.utils';
import { filter, get, includes, isEmpty, lowerCase, toUpper, upperFirst } from 'lodash-es';
import { UserInfoResponse } from 'src/types/userData.types';
import i18n from 'src/services/i18n.service';
import { NA } from 'src/consts/shared.consts';

export const getOrganizationDetails = ({ organization_details }: UserInfoResponse) =>
  organization_details;

export const getOrganizationCountryCode = (userInfo: UserInfoResponse): string => {
  const organizationDetailsObject = getOrganizationDetails(userInfo);
  return organizationDetailsObject?.country_code || organizationDetailsObject?.signup_country_code;
};

//TODO: selector isBusinessAccountOrganization
export const isBusinessAccountOrganization = (userInfo: UserInfoResponse) => {
  const organizationDetailsObject = getOrganizationDetails(userInfo);
  return !isEmpty(get(organizationDetailsObject, 'plan'));
};

//TODO: selector getOrganizationEntityId;
export const getOrganizationRapydEntityId = (userInfo: UserInfoResponse) =>
  getOrganizationDetails(userInfo)?.rapyd_entity;

//TODO: selector getIsOrganizationMinimumSettlementEUR
export const getSettlementCurrency = (userInfo: any, settlementCurrencies: any) => {
  const rapydEntity = getOrganizationRapydEntityId(userInfo);

  return (
    rapydEntity === RAPYD_ENTITIES.RAPYD_EUROPE ||
    (rapydEntity === RAPYD_ENTITIES.CASH_DASH_UK_LIMITED &&
      !isEmpty(filter(settlementCurrencies, currency => currency === 'EUR' || currency === 'GBP')))
  );
};

//Todo: selector getOrganizationSignupCountryCode
export const getOrganizationSignupCountryCode = (userInfo: UserInfoResponse) =>
  getOrganizationDetails(userInfo)?.signup_country_code;

export const getOrganizationRolePermissions = (userInfo: UserInfoResponse) => {
  const { organization_details: organizationDetails } = userInfo;
  return {
    orgRolePermissions: organizationDetails.role_permissions,
    orgProdRolePermissions: organizationDetails.prod_role_permissions,
  };
};

export const isPermittedOrganizationRole = (
  userInfo: UserInfoResponse,
  { permissions, permissionsLogicalOperator, checkProductionPermissions }: PermissionsProps,
) => {
  const { orgRolePermissions, orgProdRolePermissions } = getOrganizationRolePermissions(userInfo);
  const orgPermissions = checkProductionPermissions ? orgProdRolePermissions : orgRolePermissions;
  return isPermittedRole(orgPermissions, permissions, permissionsLogicalOperator);
};

export const isPermitted = (
  userInfo: UserInfoResponse,
  { permissions, permissionsLogicalOperator, checkProductionPermissions }: PermissionsProps,
) => {
  const isUserRolePermitted = isPermittedUserRole(userInfo, {
    permissions,
    permissionsLogicalOperator,
    checkProductionPermissions,
  });
  const isOrganizationPermitted = isPermittedOrganizationRole(userInfo, {
    permissions,
    permissionsLogicalOperator,
    checkProductionPermissions,
  });

  return isUserRolePermitted && isOrganizationPermitted;
};

export const getOrganizationRole = (userInfo: UserInfoResponse) => {
  if (!userInfo) return null;
  return getOrganizationDetails(userInfo)?.role || '';
};

export const getOrganizationProdRole = (userInfo: UserInfoResponse) => {
  if (!userInfo) return null;
  return getOrganizationDetails(userInfo)?.prod_role || '';
};

export const formatMerchantAccountName = (merchantDetails: {
  alias?: string;
  contact_firstname?: string;
  contact_lastname?: string;
}): string => {
  const { alias, contact_firstname, contact_lastname } = merchantDetails;

  if (!alias || alias === NA) {
    const prefixName =
      contact_firstname?.[0] && contact_lastname ? toUpper(contact_firstname[0]).concat('.') : '';
    const suffixName = contact_lastname
      ? upperFirst(contact_lastname)
      : upperFirst(contact_firstname) || i18n.t('MERCHANT');
    return `${prefixName}${suffixName} ${i18n.t('ACCOUNT')}`;
  }

  return alias;
};

export const isOrgProdRoleEqualToOneOfRoles = (userInfo: UserInfoResponse, roleArray: string[]) => {
  const orgProdRole = getOrganizationProdRole(userInfo);
  return includes(roleArray, orgProdRole);
};
